import EventName from '@/Enums/EventName.enum';
import { BeeswaxReportProperties } from '@/Enums/Report/BeeswaxReportProperties.enum';
import ReportView from '@/Enums/ReportingDashboard/ReportView/ReportView.enum';
import ViewType from '@/Enums/ReportingDashboard/ViewType.enum';
import { EXPAND_OPTIONS } from '@/constants/grid';
import { PUBLISHER_NAME } from '@/constants/reporting';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { gridClasses, useGridApiRef } from '@mui/x-data-grid-pro';
import PropTypes from 'prop-types';
import { useEffect, useMemo } from 'react';
import { Grid } from '../Grid';
import Pagination from '../Grid/Pagination';
import { NoRowsOverlay } from './NoRowsOverlay';
import ToolBar from './ToolBar';

const ImpressionsByTypeGrid = ({
  data = [],
  columns = [],
  loading,
  showViewSwitch,
  viewTypes,
  viewType,
  onViewTypeChange,
  currentType,
  currentTypeLabel,
  onTypeChange,
  impressionTypes,
  mapScales,
  printPdfMode,
  handleRadioToggle,
  checkedForPrintList,
  handlePrintPdfModeToggle,
  handlePrintTable,
  paginationModel,
  setPaginationModel,
  useRawPropertyNames,
  onGridRefresh,
  disabled = false,
}) => {
  const paddingLeft = useMemo(() => {
    const conditionValue = useRawPropertyNames ? ReportView.Publisher : PUBLISHER_NAME;
    return conditionValue === currentType ? '5px' : '35px';
  }, [currentType, useRawPropertyNames]);

  const totalRowData = useMemo(() => {
    const impressionField = useRawPropertyNames ? BeeswaxReportProperties.IMPRESSIONS : 'impressions';
    const spendField = useRawPropertyNames ? BeeswaxReportProperties.SPEND : 'spend';
    const clicksField = useRawPropertyNames ? BeeswaxReportProperties.CLICKS : 'clicks';

    const totalImpressions = data.reduce((sum, row) => sum + row[impressionField], 0);
    const totalSpend = data.reduce((sum, row) => sum + row[spendField], 0);
    const totalClicks = data.reduce((sum, row) => sum + row[clicksField], 0);

    return {
      totalImpressions,
      totalSpend,
      ...(viewType === ViewType.Attribution ? { totalClicks } : {}),
    };
  }, [data, useRawPropertyNames]);

  const apiRef = useGridApiRef();

  useEffect(() => {
    const handler = () => {
      requestAnimationFrame(() => {
        apiRef.current?.autosizeColumns?.({
          expand: false,
          includeHeaders: true,
          includeOutliers: false,
        });
      });
    };
    window.addEventListener(EventName.PrintAutosize, handler);
    return () => window.removeEventListener(EventName.PrintAutosize, handler);
  }, [apiRef]);

  return (
    <>
      <Grid
        apiRef={apiRef}
        sx={{
          border: 'none',
          [`& .${gridClasses.main}`]: {
            borderTopLeftRadius: '8px',
            borderTopRightRadius: '8px',
          },
          [`& .${gridClasses.cell}:first-of-type, & .${gridClasses.columnHeader}:first-of-type`]: {
            paddingLeft: paddingLeft,
            '@media print': {
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            },
          },
          [`& .${gridClasses.cell}`]: {
            fontWeight: 400,
          },
          [`& .${gridClasses.row}`]: {
            '&:hover, &.Mui-hovered': {
              backgroundColor: 'inherit',
            },
          },
          [`& .${gridClasses.row}:nth-of-type(even)`]: {
            backgroundColor: '#F8F8F8',
            '&:hover, &.Mui-hovered': {
              backgroundColor: '#F8F8F8',
            },
          },
        }}
        loading={loading}
        autosizeOnMount
        autosizeOptions={EXPAND_OPTIONS}
        disableRowSelectionOnClick
        disableColumnFilter
        disableColumnMenu
        disableColumnSelector
        rows={data}
        columns={columns}
        columnHeaderHeight={30}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        pagination
        slots={{
          pagination: Pagination,
          toolbar: ToolBar,
          columnSortedAscendingIcon: KeyboardArrowUp,
          columnSortedDescendingIcon: KeyboardArrowDown,
          noRowsOverlay: NoRowsOverlay,
        }}
        slotProps={{
          pagination: {
            loading,
            rowsPerPageOptions: [10, 25, 50],
            sx: { padding: 0 },
            paginationDisabled: printPdfMode,
            totalRowData,
          },
          footer: {
            className: 'relative',
          },
          toolbar: {
            printPdfMode,
            showViewSwitch,
            viewTypes,
            viewType,
            onViewTypeChange,
            currentType,
            currentTypeLabel,
            onTypeChange,
            impressionTypes,
            handleRadioToggle,
            mapScales,
            checkedForPrintList,
            handlePrintPdfModeToggle,
            loading,
            handlePrintTable,
            disabled,
          },
          noRowsOverlay: {
            onRefresh: onGridRefresh,
          },
        }}
      />
    </>
  );
};

ImpressionsByTypeGrid.propTypes = {
  data: PropTypes.array,
  columns: PropTypes.array,
  loading: PropTypes.bool,
  showViewSwitch: PropTypes.bool,
  viewTypes: PropTypes.array,
  viewType: PropTypes.string,
  onViewTypeChange: PropTypes.func,
  currentType: PropTypes.string,
  currentTypeLabel: PropTypes.string,
  onTypeChange: PropTypes.func,
  impressionTypes: PropTypes.array,
  mapScales: PropTypes.array,
  printPdfMode: PropTypes.bool,
  handleRadioToggle: PropTypes.func,
  checkedForPrintList: PropTypes.array,
  handlePrintPdfModeToggle: PropTypes.func,
  handlePrintTable: PropTypes.func,
  paginationModel: PropTypes.object,
  setPaginationModel: PropTypes.func,
  useRawPropertyNames: PropTypes.bool,
  onGridRefresh: PropTypes.func,
  disabled: PropTypes.bool,
};

export default ImpressionsByTypeGrid;
